import { useTranslation } from "react-i18next";
import TooltipInfo from "../../components/common/tooltip/InfoTooltip";
import TooltipInfoReq from "../../components/common/tooltip/InfoTooltipReq";
import InfoTooltipSimple from "../../components/common/tooltip/InfoTooltipSimple";
import ProfileTooltip from "../../components/common/tooltip/ProfileTooltip";
import ReasonTooltip from "../../components/common/tooltip/ReasonTooltip";
import {
  BlackGlobe,
  Globe,
  GreenSuccessCheck,
  Private,
  RedCancelIcon,
  ViewOnly,
} from "../../components/styled/AllImages";
import {
  Box,
  ButtonStyled,
  CountdownTimer,
  DateInfo,
  LazyImage,
  SpanStyled,
  StatusText,
  SubHeadingStyled,
  UserInfo,
} from "../../components/styled/Elements";
import { getRemainingTime } from "../../utils";

export const cabinetColumns = [
  { label: "id", name: "id" },
  {
    label: "privacy",
    name: "privacy",
    render: (privacyValue, row, t) => {
      let icon;
      switch (row?.common_setting?.slug) {
        case "private":
          icon = (
            <span
              className="cursor-pointer"
              data-tooltip-id="action-tooltip"
              data-tooltip-content={t("private")}
            >
              <Private />
            </span>
          );
          break;
        case "only-view":
          icon = (
            <span
              className="cursor-pointer"
              data-tooltip-id="action-tooltip"
              data-tooltip-content={t("view_only")}
            >
              <ViewOnly />
            </span>
          );
          break;
        case "open-to-all":
          icon = (
            <span
              className="cursor-pointer"
              data-tooltip-id="action-tooltip"
              data-tooltip-content={t("open_to_all_users")}
            >
              <BlackGlobe />
            </span>
          );
          break;
        default:
          icon = (
            <span
              className="cursor-pointer"
              data-tooltip-id="action-tooltip"
              data-tooltip-content={t("view_only")}
            >
              <ViewOnly />
            </span>
          );
      }

      return (
        <Box className="d-flex align-items-center justify-content-center">
          {icon}
        </Box>
      );
    },
  },
  { label: "name", name: "name" },
  {
    label: "Description",
    name: "description",
    render: (accessValue, row, t) => {
      const descData = [{ title: t("description"), value: row?.description }];

      return (
        <Box className="d-flex align-items-center justify-content-center">
          {row?.description?.trim()?.toLowerCase() !== "" ? (
            <InfoTooltipSimple data={descData} />
          ) : (
            <InfoTooltipSimple data={descData} />
          )}
        </Box>
      );
    },
  },
  {
    label: "owned_by",
    name: "owned_by",
    render: (owner, row, t, _, user) => {
      return parseInt(row?.owner_user?.id) !== parseInt(user?.id) ? (
        <UserInfo
          textAlign="start"
          name={row?.owner_user?.name}
          userRole={t(row?.role)}
          avatar={row?.owner_user?.profile}
          subLabel={row?.owner_user?.subLabel ? row?.owner_user?.subLabel : ""}
        />
      ) : (
        <UserInfo
          userId={parseInt(row?.owner_user?.id) !== parseInt(user?.id)}
          textAlign="start"
          name={row?.owner_user?.name}
          avatar={row?.owner_user?.profile}
        />
      );
    },
  },
  {
    label: "timer",
    name: "timer",
    render: (status, row, t) => {
      // if (row?.time_limit_end) {
      //   return <CountdownTimer endTime={row?.time_limit_end} />;
      // } else {
      //   return null;
      // }
      const now = new Date();
      const startTime = new Date(row?.time_limit_start);
      const endTime = new Date(row?.time_limit_end);

      if (
        row?.time_limit_start &&
        now < startTime &&
        !row?.visibility_for_temp
      ) {
        // Countdown to start time
        return <CountdownTimer endTime={startTime} />;
      } else if (row?.time_limit_end && row?.visibility_for_temp == 1) {
        // Countdown to end time
        return <CountdownTimer endTime={endTime} />;
      } else if (row?.time_limit_end && row?.visibility_for_temp == 2) {
        return <CountdownTimer endTime={0} />; // Time is over or no valid time provided
      } else {
        return null;
      }
    },
  },
  { label: "actions", name: "actions" },
];

export const receivedReqColumns = [
  { label: "id", name: "id" },
  {
    label: "status",
    name: "status",
    render: (status, _, t) => (
      <SpanStyled
        style={{
          color: status == "1" ? "var(--themeColor2)" : "var(--redColor)",
          fontWeight: "bold",
        }}
      >
        {status == "1" ? t("Open") : t("closed")}
      </SpanStyled>
    ),
  },
  {
    label: "requester",
    name: "requester",
    render: (requester, row, t) => (
      <UserInfo
        textAlign="start"
        name={requester?.name}
        userRole={t(requester?.role)}
        avatar={requester?.avatar}
        subLabel={requester?.subLabel}
      />
    ),
  },
  {
    label: "access_requested_for",
    name: "accessRequestedFor",
    render: (accessValue, row, t) => (
      <Box className="d-flex align-items-center justify-content-center gap-2">
        <TooltipInfoReq data={accessValue?.infoData} />
        {t(accessValue?.utilityName)}
      </Box>
    ),
  },
  {
    label: "decision",
    name: "decision",
    render: (
      decision,
      row,
      t,
      setShowModal,
      acceptRejectRequest,
      setRequestId
    ) => (
      <Box>
        {decision == "1" ? (
          <Box className="d-flex">
            <SpanStyled
              color="var(--themeColor2)"
              border="var(--themeColor2)"
              weight={700}
              radius="10px"
              className="py-2 px-3"
            >
              <Box className="d-flex align-items-center gap-1">
                <GreenSuccessCheck />
                {t("approved")}
              </Box>
            </SpanStyled>
          </Box>
        ) : decision == "2" ? (
          <Box className="d-flex align-items-center gap-2">
            <SpanStyled
              color="var(--redColor)"
              border="var(--redColor)"
              weight={700}
              radius="10px"
              className="py-2 px-3"
            >
              <Box className="d-flex align-items-center gap-1">
                <RedCancelIcon />
                {t("rejected")}
              </Box>
            </SpanStyled>
            <ReasonTooltip data={row.reason} />
          </Box>
        ) : (
          <Box className="d-flex gap-2">
            <ButtonStyled
              onClick={() => acceptRejectRequest(row.id, 1)}
              bg="var(--themeColor2)"
              size="14px"
              label={t("approve")}
            />
            <ButtonStyled
              bg="var(--redColor)"
              size="14px"
              label={t("reject")}
              onClick={() => {
                setShowModal(true);
                setRequestId(row.id);
              }}
            />
          </Box>
        )}
      </Box>
    ),
  },
  {
    label: "close_date",
    name: "closeDate",
    render: (date) => (date ? <DateInfo date={date} /> : "-"),
  },
];
export const sentReqColumns = [
  { label: "id", name: "id" },
  {
    label: "status",
    name: "status",
    render: (status, _, t) => (
      <SpanStyled
        style={{
          color: status == "1" ? "var(--themeColor2)" : "var(--redColor)",
          fontWeight: "bold",
        }}
      >
        {status == "1" ? t("Open") : t("closed")}
      </SpanStyled>
    ),
  },
  {
    label: "benefactor",
    name: "benefactor",
    render: (benefactor, row, t) => (
      <UserInfo
        textAlign="start"
        name={benefactor?.name}
        userRole={t(benefactor?.role)}
        avatar={benefactor?.avatar}
        subLabel={benefactor.subLabel}
      />
    ),
  },
  {
    label: "access_requested_for",
    name: "accessRequestedFor",
    render: (accessValue, row, t) => (
      <Box className="d-flex align-items-center justify-content-center gap-2">
        <TooltipInfoReq data={accessValue.infoData} />
        {t(accessValue.utilityName)}
      </Box>
    ),
  },
  {
    label: "decision",
    name: "decision",
    render: (decision, row, t, setCancelModal, setRequestId) => (
      <Box>
        {decision == "1" ? (
          <Box className="d-flex">
            <SpanStyled
              color="var(--themeColor2)"
              border="var(--themeColor2)"
              weight={700}
              radius="10px"
              className="py-2 px-3"
            >
              <Box className="d-flex align-items-center gap-1">
                <GreenSuccessCheck />
                {t("approved")}
              </Box>
            </SpanStyled>
          </Box>
        ) : decision == "2" ? (
          <Box className="d-flex align-items-center gap-2">
            <SpanStyled
              color="var(--redColor)"
              border="var(--redColor)"
              weight={700}
              radius="10px"
              className="py-2 px-3"
            >
              <Box className="d-flex align-items-center gap-1">
                <RedCancelIcon />
                {t("rejected")}
              </Box>
            </SpanStyled>
            <ReasonTooltip data={row.reason} />
          </Box>
        ) : (
          <Box className="d-flex gap-2">
            <ButtonStyled
              bg="var(--redColor)"
              size="14px"
              label={t("Cancel")}
              onClick={() => {
                setRequestId(row.id);
                setCancelModal(true);
              }}
            />
          </Box>
        )}
      </Box>
    ),
  },
  {
    label: "close_date",
    name: "closeDate",
    render: (date) => (date ? <DateInfo date={date} /> : "-"),
  },
];
export const historyColumns = [
  {
    label: "id",
    name: "id",
  },
  {
    label: "date",
    name: "date",
    render: (date) => (date ? <DateInfo date={date} /> : "-"),
  },
  {
    label: "number",
    name: "number",
  },
  {
    label: "archive_title",
    name: "archiveTitle",
  },
  {
    label: "no_of_view",
    name: "views",
  },
  {
    label: "no_of_downloads",
    name: "downloads",
  },
  {
    label: "actions",
    name: "actions",
  },
];
export const historyDetailColumns = [
  {
    label: "id",
    name: "id",
  },
  {
    label: "date",
    name: "date",
    render: (date, row) =>
      date ? (
        <Box className="d-flex justify-content-center gap-1">
          <DateInfo date={date} /> -{" "}
          <SpanStyled dir="ltr">{row?.time}</SpanStyled>
        </Box>
      ) : (
        "-"
      ),
  },
  {
    label: "username",
    name: "username",
  },
  {
    label: "user_info",
    name: "user_info",
    render: (userInfo) =>
      Number(userInfo?.user_detail) === 6 ? null : (
        <Box className="d-flex justify-content-center">
          <ProfileTooltip
            data={userInfo.data}
            userName={userInfo.name}
            userRole={userInfo.role}
            userImage={userInfo.img}
            subLabel={userInfo.subLabel}
          />
        </Box>
      ),
  },
  {
    label: "view",
    name: "view",
    render: (view) => (view ? <GreenSuccessCheck /> : <RedCancelIcon />),
  },
  {
    label: "download",
    name: "download",
    render: (download) =>
      download ? <GreenSuccessCheck /> : <RedCancelIcon />,
  },
];
export const usersListColumns = [
  {
    label: "id",
    name: "id",
  },
  {
    label: "status",
    name: "status",
    render: (status, row, t) => (
      <Box className="d-flex justify-content-center">
        {status == "1" ? (
          <StatusText status={status} text={t("active")} />
        ) : (
          <StatusText status={status} text={t("deactive")} />
        )}
      </Box>
    ),
  },
  {
    label: "type",
    name: "user_type_id",
    render: (type, row, t) => (parseInt(type) === 3 ? t("admin") : t("user")),
  },
  {
    label: "name",
    name: "name",
    render: (user, row, t, lang) => (
      <Box className="d-flex justify-content-start">
        <UserInfo
          name={user}
          role={
            lang === "en" ? row?.department?.name_en : row?.department?.name_ar
          }
          avatar={row?.profile}
        />
      </Box>
    ),
  },
  {
    label: "department",
    name: "department",
    render: (department, _, t, lang) => (
      <Box>
        <SubHeadingStyled
          size="14px"
          color="var(--text)"
          weight="bold"
          className="text-nowrap"
        >
          {department?.dep}
        </SubHeadingStyled>
        <SpanStyled size="12px" weight="light" color="var(--text2)">
          {department?.subLabel}
        </SpanStyled>
      </Box>
    ),
  },
  // {
  //   label: "email",
  //   name: "email",
  // },
  // {
  //   label: "phone",
  //   name: "phone",
  //   render: (phone) => (
  //     <Box
  //       className="text-nowrap"
  //       style={{
  //         direction: "ltr",
  //         unicodeBidi: "isolate",
  //         display: "inline",
  //       }}
  //     >
  //       {phone}
  //     </Box>
  //   ),
  // },
  {
    label: "actions",
    name: "actions",
  },
];
export const tempUsersListColumns = [
  {
    label: "id",
    name: "id",
  },
  {
    label: "status",
    name: "status",
    render: (status, row, t) => (
      <Box className="d-flex justify-content-center">
        {row?.is_expired == 1 ? (
          <StatusText status={0} text={t("expired")} />
        ) : status == "1" ? (
          <StatusText status={status} text={t("active")} />
        ) : (
          <StatusText status={status} text={t("deactive")} />
        )}
      </Box>
    ),
  },
  {
    label: "name",
    name: "name",
    render: (user, row, t, lang) => (
      <Box className="d-flex justify-content-start">
        <UserInfo
          name={user}
          role={
            lang === "en" ? row?.department?.name_en : row?.department?.name_ar
          }
          avatar={row?.profile}
        />
      </Box>
    ),
  },
  {
    label: "email",
    name: "email",
  },
  // {
  //   label: "phone",
  //   name: "phone",
  //   render: (phone) => (
  //     <Box
  //       className="text-nowrap"
  //       style={{
  //         direction: "ltr",
  //         unicodeBidi: "isolate",
  //         display: "inline",
  //       }}
  //     >
  //       {phone}
  //     </Box>
  //   ),
  // },
  {
    label: "actions",
    name: "actions",
  },
];
export const archiveColumns = [
  {
    label: "id",
    name: "id",
  },
  {
    label: "privacy",
    name: "privacy",
    render: (privacyValue, row, t) => {
      let icon;
      switch (row?.common_setting?.slug) {
        case "private":
          icon = (
            <span
              className="cursor-pointer"
              data-tooltip-id="action-tooltip"
              data-tooltip-content={t("private")}
            >
              <Private />
            </span>
          );
          break;
        case "only-view":
          icon = (
            <span
              className="cursor-pointer"
              data-tooltip-id="action-tooltip"
              data-tooltip-content={t("view_only")}
            >
              <ViewOnly />
            </span>
          );
          break;
        case "open-to-all":
          icon = (
            <span
              className="cursor-pointer"
              data-tooltip-id="action-tooltip"
              data-tooltip-content={t("open_to_all_users")}
            >
              <BlackGlobe />
            </span>
          );
          break;
        default:
          icon = (
            <span
              className="cursor-pointer"
              data-tooltip-id="action-tooltip"
              data-tooltip-content={t("view_only")}
            >
              <ViewOnly />
            </span>
          );
      }

      return (
        <Box className="d-flex justify-content-center align-items-center">
          {icon}
        </Box>
      );
    },
  },
  {
    label: "date",
    name: "date",
    render: (date) => (date ? <DateInfo date={date} /> : "-"),
  },
  {
    label: "title",
    name: "title",
  },
  {
    label: "Description",
    name: "description",
    render: (accessValue, row, t) => {
      const descData = [{ title: t("description"), value: row?.description }];

      return (
        <Box className="d-flex justify-content-center align-items-center gap-2">
          {row?.description?.trim()?.toLowerCase() !== "" ? (
            <InfoTooltipSimple data={descData} />
          ) : (
            <InfoTooltipSimple data={descData} />
          )}
        </Box>
      );
    },
  },
  {
    label: "owned_by",
    name: "owned_by",
    render: (owner, row, t, _, user) => {
      return (
        <Box className="d-flex justify-content-start">
          {parseInt(row?.owner_user?.id) !== parseInt(user?.id) ? (
            <UserInfo
              textAlign="start"
              name={row?.owner_user?.name}
              // userRole={t(row?.role)}
              avatar={row?.owner_user?.profile}
              subLabel={
                row?.owner_user?.subLabel ? row?.owner_user?.subLabel : ""
              }
            />
          ) : (
            <UserInfo
              userId={parseInt(row?.owner_user?.id) !== parseInt(user?.id)}
              textAlign="start"
              name={row?.owner_user?.name}
              avatar={row?.owner_user?.profile}
            />
          )}
        </Box>
      );
    },
  },
  {
    label: "number",
    name: "number",
  },
  {
    label: "from",
    name: "from",
  },
  {
    label: "to",
    name: "to",
  },
  {
    label: "actions",
    name: "actions",
  },
];

export const userPermissionColumns = [
  {
    id: 0,
    title: "",
    name: "module_name",
  },
  {
    id: "all",
    title: "all",
    name: "all",
  },
  {
    id: 1,
    title: "Open",
    name: "open",
  },
  {
    id: 2,
    title: "Create",
    name: "create",
  },
  {
    id: 3,
    title: "view",
    name: "view",
  },
  {
    id: 4,
    title: "edit",
    name: "edit",
  },
  {
    id: 5,
    title: "view_history",
    name: "view_history",
  },
];
export const userDtlPermissionColumns = [
  {
    title: "",
    name: "module_name",
  },
  // {
  //   title: "all",
  //   name: "all",
  // },
  {
    title: "Open",
    name: "open",
  },
  {
    title: "Create",
    name: "create",
  },
  {
    title: "view",
    name: "view",
  },
  {
    title: "edit",
    name: "edit",
  },
  {
    title: "access_log",
    name: "view_history",
  },
  {
    title: "download",
    name: "download",
  },
];

export const tempTUserDtlPermissionColumns = [
  {
    title: "",
    name: "module_name",
  },
  // {
  //   title: "all",
  //   name: "all",
  // },
  // {
  //   title: "view_only",
  //   name: "view_only",
  // },
  {
    title: "view_and_open",
    name: "view_and_open",
  },
];
export const tempPermissionsCols = [
  {
    title: "",
    name: "",
  },
  // {
  //   title: "view_only",
  //   name: "view_only",
  // },
  {
    title: "view_and_open",
    name: "view_and_open",
  },
];

export const hqBranchColumns = [
  // {
  //   label: "id",
  //   name: "id",
  // },
  {
    label: "type",
    name: "type",
  },
  {
    label: "name",
    name: "name",
    render: (name, _, t) => (
      <SpanStyled size="16px" weight="800" color="var(--text)">
        {t(name)}
      </SpanStyled>
    ),
  },
  {
    label: "City",
    name: "city",
    render: (city) => city || "-",
  },
  {
    label: "no_of_departments",
    name: "no_of_departments",
  },
  {
    label: "no_of_users",
    name: "no_of_users",
  },
  {
    label: "actions",
    name: "actions",
  },
];

export const branchesColumns = [
  {
    label: "BranchnameEN",
    name: "BranchnameEN",
  },
  {
    label: "BranchnameAR",
    name: "BranchnameAR",
  },
  {
    label: "City",
    name: "City",
  },
];
export const headquartersColumns = [
  {
    label: "HeadquarternameEN",
    name: "HeadquarternameEN",
  },
  {
    label: "HeadquarternameAR",
    name: "HeadquarternameAR",
  },
  {
    label: "City",
    name: "City",
  },
];
export const tempADaccessColumns = [
  {
    label: "UserName",
    name: "user",
    render: (user, row, t, language) => (
      <UserInfo
        userRole={user.role}
        name={user.name}
        subLabel={user.subLabel}
        avatar={user.image}
      />
    ),
    /* render: (user) => (
      <UserInfo name={user.name} userDepartment={user.userDepartment} userBranch={user.userBranch} avatar={user.image} />
    ), */
  },
  {
    label: "startDate",
    name: "startDate",
  },
  {
    label: "endDate",
    name: "endDate",
  },
];

export const adminsListColumns = [
  {
    label: "id",
    name: "id",
  },
  {
    label: "type",
    name: "type",
  },
  {
    label: "department",
    name: "department",
    render: (department, _, t) => {
      const departmentInfo = department.department_info;
      return (
        <Box className="d-flex justify-content-between gap-1">
          <Box>
            <Box className="d-flex align-items-center">
              <SubHeadingStyled
                size="14px"
                color="var(--text)"
                weight="bold"
                className="text-nowrap"
              >
                {t(department.name)}
              </SubHeadingStyled>
            </Box>
            <SpanStyled size="12px" weight="light" color="var(--text2)">
              {department.branch}
            </SpanStyled>
          </Box>
          {/* <TooltipInfo data={departmentInfo} /> */}
        </Box>
      );
    },
  },
  {
    label: "admin_name",
    name: "user",
    render: (user) => (
      <Box className="d-flex align-items-center justify-content-center gap-2">
        <ProfileTooltip
          data={user.user_info}
          userName={user.userName}
          userRole={user.userRole}
          userImage={user.image}
        />
        {/* <UserInfo name={user.name} userRole={user.userRole} avatar={user.image} /> */}
        <UserInfo name={user.name} avatar={user.image} />
      </Box>
    ),
  },
  {
    label: "no_of_licenses",
    name: "no_of_licenses",
  },
  {
    label: "no_of_users",
    name: "no_of_users",
  },
  {
    label: "no_of_licenses_left",
    name: "no_of_licenses_left",
  },
  {
    label: "actions",
    name: "actions",
  },
];

export const adminsViewsListColumns = [
  {
    label: "id",
    name: "id",
  },
  {
    label: "status",
    name: "status",
    render: (status, row, t) =>
      status == "1" ? (
        <StatusText status={status} text={t("active")} />
      ) : (
        <StatusText status={status} text={t("deactive")} />
      ),
  },
  {
    label: "user",
    name: "user",
    render: (user) => (
      <UserInfo name={user.name} userRole={user.userRole} avatar={user.image} />
    ),
  },
  {
    label: "actions",
    name: "actions",
  },
];

export const departmentColumns = [
  {
    label: "id",
    name: "id",
  },
  {
    label: "name",
    name: "name",
    render: (name, row, t, language) =>
      language === "en" ? row?.name_en : row?.name_ar,
  },
  {
    label: "City",
    name: "city",
    render: (city) => city || "-",
  },
  // {
  //   label: "contact_email",
  //   name: "email",
  // },
  // {
  //   label: "contact_phone",
  //   name: "contact_no",
  //   render: (phone) => (
  //     <Box
  //       className="text-nowrap"
  //       style={{
  //         direction: "ltr",
  //         unicodeBidi: "isolate",
  //         display: "inline",
  //       }}
  //     >
  //       {phone}
  //     </Box>
  //   ),
  // },
  {
    label: "actions",
    name: "actions",
  },
];
export const editTempADaccessColumns = [
  {
    label: "UserName",
    name: "user",
    render: (user, row, t, language) => (
      <UserInfo name={user.name} subLabel={user.subLabel} avatar={user.image} />
    ),
  },
  {
    label: "startDate",
    name: "startDate",
  },
  {
    label: "endDate",
    name: "endDate",
  },
  {
    label: "actions",
    name: "actions",
  },
];

export const FilesColumns = [
  { label: "id", name: "id" },
  {
    label: "privacy",
    name: "privacy",
    render: (privacyValue, row, t) => {
      let icon;
      switch (row?.common_setting?.slug) {
        case "private":
          icon = (
            <span
              className="cursor-pointer"
              data-tooltip-id="action-tooltip"
              data-tooltip-content={t("private")}
            >
              <Private />
            </span>
          );
          break;
        case "only-view":
          icon = (
            <span
              className="cursor-pointer"
              data-tooltip-id="action-tooltip"
              data-tooltip-content={t("view_only")}
            >
              <ViewOnly />
            </span>
          );
          break;
        case "open-to-all":
          icon = (
            <span
              className="cursor-pointer"
              data-tooltip-id="action-tooltip"
              data-tooltip-content={t("open_to_all_users")}
            >
              <BlackGlobe />
            </span>
          );
          break;
        default:
          icon = (
            <span
              className="cursor-pointer"
              data-tooltip-id="action-tooltip"
              data-tooltip-content={t("view_only")}
            >
              <ViewOnly />
            </span>
          );
      }

      return (
        <Box className="d-flex justify-content-center align-items-center">
          {icon}
        </Box>
      );
    },
  },
  { label: "FileName", name: "name" },
  { label: "NoofArchives", name: "no_of_archives" },
  {
    label: "Description",
    name: "description",
    render: (accessValue, row, t) => {
      const descData = [{ title: t("description"), value: row?.description }];

      return (
        <Box className="d-flex justify-content-center align-items-center gap-2">
          {row?.description?.trim()?.toLowerCase() !== "" ? (
            <InfoTooltipSimple data={descData} />
          ) : (
            <InfoTooltipSimple data={descData} />
          )}
        </Box>
      );
    },
  },
  {
    label: "owned_by",
    name: "owned_by",
    render: (owner, row, t, _, user) => {
      return parseInt(row?.owner_user?.id) !== parseInt(user?.id) ? (
        <UserInfo
          textAlign="start"
          name={row?.owner_user?.name}
          userRole={t(row?.role)}
          avatar={row?.profile}
          subLabel={row?.owner_user?.subLabel ? row?.owner_user?.subLabel : ""}
        />
      ) : (
        <UserInfo
          userId={parseInt(row?.owner_user?.id) !== parseInt(user?.id)}
          textAlign="start"
          name={row?.owner_user?.name}
          avatar={row?.profile}
        />
      );
    },
  },
  { label: "actions", name: "actions" },
];

export const ReferralsListColumns = [
  {
    label: "date_time",
    name: "date_time",
  },
  {
    label: "cabinet_name",
    name: "cabinet_name",
  },
  {
    label: "file_name",
    name: "file_name",
  },
  {
    label: "archive_name",
    name: "archive_name",
  },
  {
    label: "user_name",
    name: "user_name",
  },
  {
    label: "status",
    name: "status",
    render: (status, _, t) => (
      <SpanStyled
        style={{
          color: status == "1" ? "var(--themeColor2)" : "var(--redColor)",
          fontWeight: "bold",
        }}
      >
        {status == "1" ? t("Seen") : t("Unseen")}
      </SpanStyled>
    ),
  },
  {
    label: "actions",
    name: "actions",
  },
];
